import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiSend } from '@jmvilaca-corp/auth-ui'
import { eventsKey } from './calendars'
import { fromDayKey, formatMoment } from './dates'

// Rappel d'un evenement : chacun a le sien. Le serveur le garde en minutes
// avant le debut. Pour un evenement sur la journee, qui commence a minuit UTC,
// c'est l'appareil qui traduit « la veille a 18 h » : lui seul connait son fuseau.

export const minReminderMinutes = 2
export const defaultReminderMinutes = 15

/** Delais proposes pour un evenement horodate, en minutes avant son debut. */
export const reminderPresets = [5, 10, 15, 30, 60, 90, 120, 24 * 60] as const

export type ReminderUnit = 'minutes' | 'hours' | 'days'

export const reminderUnits: { unit: ReminderUnit; label: string; minutes: number }[] = [
  { unit: 'minutes', label: 'minutes', minutes: 1 },
  { unit: 'hours', label: 'heures', minutes: 60 },
  { unit: 'days', label: 'jours', minutes: 24 * 60 },
]

/** Moment d'un rappel d'evenement sur la journee : tant de jours avant le premier jour, a telle heure d'ici. */
export interface AllDayMoment {
  daysBefore: number
  hour: number
  minute: number
}

export const allDayPresets: (AllDayMoment & { label: string })[] = [
  { daysBefore: 0, hour: 8, minute: 0, label: 'Le jour meme a 8 h' },
  { daysBefore: 1, hour: 18, minute: 0, label: 'La veille a 18 h' },
  { daysBefore: 1, hour: 9, minute: 0, label: 'La veille a 9 h' },
  { daysBefore: 2, hour: 18, minute: 0, label: '2 jours avant a 18 h' },
  { daysBefore: 7, hour: 9, minute: 0, label: 'Une semaine avant a 9 h' },
]

export const defaultAllDayMoment: AllDayMoment = { daysBefore: 1, hour: 18, minute: 0 }

/** Ce que le formulaire retient : rien, un delai, ou un moment pour un evenement sur la journee. */
export type ReminderChoice =
  | { kind: 'none' }
  | { kind: 'minutes'; minutes: number }
  | ({ kind: 'allDay' } & AllDayMoment)

const DAY_MS = 24 * 60 * 60 * 1000

function allDayStart(firstDay: string): number {
  return Date.parse(`${firstDay}T00:00:00.000Z`)
}

function allDayDue(firstDay: string, moment: AllDayMoment): Date {
  const due = fromDayKey(firstDay)
  due.setDate(due.getDate() - moment.daysBefore)
  due.setHours(moment.hour, moment.minute, 0, 0)
  return due
}

/** Minutes avant le debut a envoyer au serveur ; `null` sans rappel, `NaN` pour une saisie illisible. */
export function choiceToMinutes(choice: ReminderChoice, firstDay: string): number | null {
  switch (choice.kind) {
    case 'none':
      return null
    case 'minutes':
      return choice.minutes
    case 'allDay':
      return Math.round((allDayStart(firstDay) - allDayDue(firstDay, choice).getTime()) / 60000)
  }
}

export function choiceFromMinutes(
  minutes: number | null,
  event: { isAllDay: boolean; firstDay: string },
): ReminderChoice {
  if (minutes === null) {
    return { kind: 'none' }
  }

  if (!event.isAllDay) {
    return { kind: 'minutes', minutes }
  }

  const due = new Date(allDayStart(event.firstDay) - minutes * 60000)
  const firstDay = fromDayKey(event.firstDay)
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate())

  return {
    kind: 'allDay',
    daysBefore: Math.round((firstDay.getTime() - dueDay.getTime()) / DAY_MS),
    hour: due.getHours(),
    minute: due.getMinutes(),
  }
}

/** Instant ou le rappel doit partir. */
export function reminderDueAt(startsAt: string, minutesBefore: number): Date {
  return new Date(new Date(startsAt).getTime() - minutesBefore * 60000)
}

/**
 * Ce qui empeche d'enregistrer ce rappel, ou `null`. Confort de saisie : le
 * serveur refait le controle, et c'est son horloge qui decide.
 */
export function reminderError(minutes: number, event: { startsAt: string; isAllDay: boolean }, now: Date): string | null {
  if (!Number.isInteger(minutes)) {
    return 'Indiquez un delai en chiffres.'
  }

  if (!event.isAllDay && minutes < minReminderMinutes) {
    return `Un rappel se regle au moins ${minReminderMinutes} minutes avant le debut.`
  }

  return reminderDueAt(event.startsAt, minutes).getTime() <= now.getTime() ? "L'heure de ce rappel est deja passee." : null
}

/** « 15 min », « 1 h 30 », « 1 jour ». */
export function shortDelay(minutes: number): string {
  if (minutes % (24 * 60) === 0) {
    const days = minutes / (24 * 60)
    return `${days} jour${days > 1 ? 's' : ''}`
  }

  if (minutes < 60) {
    return `${minutes} min`
  }

  const rest = minutes % 60
  return rest === 0 ? `${minutes / 60} h` : `${Math.floor(minutes / 60)} h ${String(rest).padStart(2, '0')}`
}

export function formatDelay(minutes: number): string {
  return `${shortDelay(minutes)} avant`
}

/** Le rappel choisi, en quelques mots ; `null` sans rappel, ou tant que la saisie n'est pas lisible. */
export function choiceLabel(choice: ReminderChoice, firstDay: string): string | null {
  switch (choice.kind) {
    case 'none':
      return null
    case 'minutes':
      return Number.isInteger(choice.minutes) ? shortDelay(choice.minutes) : null
    case 'allDay':
      return formatAllDayMoment(choice, firstDay)
  }
}

/** Le rappel propose d'office a qui en demande un sans dire lequel. */
export function defaultReminder(isAllDay: boolean): ReminderChoice {
  return isAllDay ? { kind: 'allDay', ...defaultAllDayMoment } : { kind: 'minutes', minutes: defaultReminderMinutes }
}

/** Le moment en clair : celui d'un choix propose, sinon le jour et l'heure. */
export function formatAllDayMoment(moment: AllDayMoment, firstDay: string): string {
  return (
    allDayPresets.find((preset) => sameMoment(preset, moment))?.label ??
    formatMoment(allDayDue(firstDay, moment).toISOString())
  )
}

export function sameMoment(left: AllDayMoment, right: AllDayMoment): boolean {
  return left.daysBefore === right.daysBefore && left.hour === right.hour && left.minute === right.minute
}

/** Unite la plus grande qui exprime ce delai sans virgule. */
export function splitDelay(minutes: number): { amount: number; unit: ReminderUnit } {
  const fitting = [...reminderUnits].reverse().find((item) => minutes % item.minutes === 0) ?? reminderUnits[0]!
  return { amount: minutes / fitting.minutes, unit: fitting.unit }
}

export function joinDelay(amount: string, unit: ReminderUnit): number {
  const value = /^\d+$/.test(amount.trim()) ? Number(amount.trim()) : Number.NaN
  return value * (reminderUnits.find((item) => item.unit === unit)?.minutes ?? 1)
}

/** Pose ou regle le rappel de l'utilisateur sur un evenement ; `null` le retire. */
export function useSaveReminder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ eventId, minutesBefore }: { eventId: string; minutesBefore: number | null }) =>
      minutesBefore === null
        ? apiSend<null>(`/api/events/${eventId}/reminder`, 'DELETE')
        : apiSend<{ minutesBefore: number }>(`/api/events/${eventId}/reminder`, 'PUT', { minutesBefore }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: eventsKey }),
  })
}
